/**
 * +glide: wind streaks around a gliding (or fast-falling) hero: thin additive quads that slip past
 * the body against the flight, longer and brighter with speed. One instanced draw; a small pool
 * recycled around the followed hero only (the one the camera rides with).
 */
import * as THREE from 'three';
import { U } from '../style';

const N = 36;

interface Streak {
  life: number;
  max: number;
  x: number;
  y: number;
  z: number;
}

export class WindStreaks {
  readonly mesh: THREE.InstancedMesh;
  private readonly pool: Streak[] = Array.from({ length: N }, () => ({ life: 0, max: 1, x: 0, y: 0, z: 0 }));
  private readonly alpha = new Float32Array(N);
  private readonly aAlpha: THREE.InstancedBufferAttribute;
  private spawnAcc = 0;
  private readonly m = new THREE.Matrix4();
  private readonly dir = new THREE.Vector3();
  private readonly side = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly toCam = new THREE.Vector3();
  private readonly along = new THREE.Vector3();

  constructor() {
    const geo = new THREE.PlaneGeometry(1, 1);
    this.aAlpha = new THREE.InstancedBufferAttribute(this.alpha, 1);
    geo.setAttribute('aAlpha', this.aAlpha);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uFx: U.uFx },
      vertexShader: `attribute float aAlpha; varying vec2 vUv; varying float vA;
        void main(){ vUv = uv; vA = aAlpha; gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0); }`,
      fragmentShader: `uniform float uFx; varying vec2 vUv; varying float vA;
        void main(){
          float across = 1.0 - abs(vUv.y * 2.0 - 1.0);
          float along = smoothstep(0.0, 0.35, vUv.x) * (1.0 - smoothstep(0.55, 1.0, vUv.x));
          float a = across * across * along * vA * 0.55 * min(1.2, uFx);
          gl_FragColor = vec4(vec3(0.92, 0.98, 1.0) * a, a);
        }`,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, N);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.renderOrder = 5;
  }

  /**
   * `at`: the hero's chest (three.js world), `vel`: flight velocity (three.js world, yd/s), `on`:
   * gliding or falling fast. Streak rate and length grow with speed.
   */
  update(dtS: number, at: THREE.Vector3 | null, vel: THREE.Vector3, on: boolean, camera: THREE.Camera): void {
    const speed = vel.length();
    const k = on && at ? Math.min(1, Math.max(0, (speed - 4) / 10)) : 0;
    this.spawnAcc += dtS * 40 * k;
    for (const s of this.pool) {
      if (this.spawnAcc < 1 || !at) break;
      if (s.life > 0) continue;
      this.spawnAcc -= 1;
      const a = Math.random() * Math.PI * 2;
      const r = 0.6 + Math.random() * 1.6;
      s.x = at.x + Math.cos(a) * r + vel.x * 0.12;
      s.y = at.y + Math.sin(a) * r * 0.8 + 0.4 + vel.y * 0.12;
      s.z = at.z + Math.sin(a * 1.7) * r + vel.z * 0.12;
      s.max = s.life = 0.28 + Math.random() * 0.22;
    }
    if (this.spawnAcc > 4) this.spawnAcc = 0;
    if (speed > 1e-3) this.dir.copy(vel).multiplyScalar(-1 / speed);
    else this.dir.set(1, 0, 0);
    const len = 0.8 + speed * 0.16;
    let n = 0;
    for (const s of this.pool) {
      if (s.life <= 0) continue;
      s.life -= dtS;
      if (s.life <= 0) continue;
      // The air stands still: the streak stays where it was born while the hero flies past it.
      this.toCam.set(camera.position.x - s.x, camera.position.y - s.y, camera.position.z - s.z).normalize();
      this.side.crossVectors(this.dir, this.toCam).normalize().multiplyScalar(0.035 + 0.02 * k);
      this.up.crossVectors(this.dir, this.side).normalize();
      this.m.makeBasis(this.along.copy(this.dir).multiplyScalar(len), this.side, this.up).setPosition(s.x, s.y, s.z);
      this.mesh.setMatrixAt(n, this.m);
      const t = s.life / s.max;
      this.alpha[n] = Math.sin(t * Math.PI) * (0.5 + 0.5 * k);
      n++;
    }
    this.mesh.count = n;
    if (n) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.aAlpha.needsUpdate = true;
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.removeFromParent();
  }
}
